import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { formatDateTimeBadge } from '../../../utils';
import { JOB_KIND_COLOR_MAP } from '../../../consts';

export const useJobTaskServiceHistoryColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return useMemo(() => [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "job__number",
      headerName: t("Number"),
      valueGetter: (_value, row) => row.job?.number || "-",
    },
    {
      field: "job__kind",
      headerName: t("Job Kind"),
      valueGetter: (_value, row) => row.job?.kind || "-",
      renderCell: (params) => {
        const kindCode = params.row.job?.kind_code;
        const label = params.row.job?.kind || "-";
        return (
          <Chip
            label={label}
            color={JOB_KIND_COLOR_MAP[kindCode] ?? "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "job_created_by",
      headerName: t("Created By"),
      sortable: false,
      valueGetter: (_value, row) => {
        const createdBy = row.job?.created_by;
        if (!createdBy) return "-";
        const name = [createdBy.first_name, createdBy.last_name].filter(Boolean).join(' ');
        return name || createdBy.email || "-";
      }
    },
    {
      field: "issues",
      headerName: t("Issues / Services"),
      sortable: false,
      renderCell: (params) => {
        const issues = params.row.issues || [];
        const services = params.row.services || [];
        const items: { label: string; color?: string | null }[] = [
          ...(Array.isArray(issues) ? issues.map((i: { code?: string | null; title?: string; color?: string | null }) => ({
            label: i.code ? `${i.title || "—"} (${i.code})` : (i.title || "—"),
            color: i.color,
          })) : []),
          ...(Array.isArray(services) ? services.map((s: { title?: string }) => ({
            label: s.title || "—",
            color: null,
          })) : []),
        ];
        if (items.length === 0) return "—";
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
            {items.map((item, index) => (
              <Chip
                key={index}
                label={item.label}
                size="small"
                sx={
                  item.color
                    ? {
                        bgcolor: item.color,
                        color: "white",
                        "& .MuiChip-label": { color: "white" },
                      }
                    : {
                        bgcolor: "rgba(128, 128, 128, 0.25)",
                        color: "text.secondary",
                      }
                }
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "job_assigned_to_names",
      headerName: t("Assigned To"),
      sortable: false,
      valueGetter: (_value, row) => {
        const names = row.job?.assigned_to_names;
        if (Array.isArray(names) && names.length > 0) {
          return names.filter(Boolean).join(", ");
        }
        return "-";
      },
      renderCell: (params) => {
        const names = params.row.job?.assigned_to_names;
        if (!Array.isArray(names) || names.length === 0) {
          return "—";
        }
        const filtered = names.filter(Boolean);
        if (filtered.length === 0) {
          return "—";
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
              width: "100%",
              height: "100%",
              minHeight: 52,
            }}
          >
            {filtered.map((name: string, index: number) => (
              <Chip key={`${name}-${index}`} label={name} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      field: "job__finished_at",
      headerName: t("Finished At"),
      renderCell: (params) => params.row.job?.finished_at ? formatDateTimeBadge(params.row.job?.finished_at) : "-",
    },
  ], [t]);
};
